import { Alert, Button, ButtonGroup, Chip } from '@mui/material';
import { ColDef, GetRowIdParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import { CommodityManagementApi } from '../../../../Apis/Apis';
import {
  CommodityContractMonthMappingModel,
  listCommodityContractMonthMappings,
  updateCommodityContractMonthMappings,
} from '../../../../Apis/ContractMonthMappingApi';
import { CommodityModel, Unit } from '../../../../Generated/Commodity-Management-Api';
import {
  ContractMonthMappingRow,
  buildContractMonthMappingRows,
  commodityHasContractMonthMapping,
  toUpdateContractMonthMappings,
} from '../../../../Helpers';
import { useLoadingState } from '../../../../Hooks';
import { useApplicationDispatch } from '../../../../Redux/ReduxStore';
import {
  addCommodity as addCommodityRedux,
  updateCommodity as updateCommodityRedux,
  removeCommodity as removeCommodityRedux,
} from '../../../../Redux/Slices/CommodityManagementSlice';
import { StoneXGrid, StoneXRow } from '../../../StoneX';
import { StoneXModal, StoneXSelect, StoneXTextField } from '../../../StoneXMui';
import ContractMonthMappingTable from './ContractMonthMappingTable';

interface CommoditySettingsProps {
  commodities: CommodityModel[];
}

const defaultDisplayPrecision = 2;

export default function CommoditySettings(props: CommoditySettingsProps) {
  const dispatch = useApplicationDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [commodities, setCommodities] = useState<CommodityModel[]>(props.commodities ?? []);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [modalCommodity, setModalCommodity] = useState<CommodityModel | undefined>();
  const saveCommodityLoadingState = useLoadingState();
  const gridRef = useRef<AgGridReact<CommodityModel>>(null);

  // Budget month -> contract month mappings for the whole site, so the grid can show
  // which commodities have one without a request per row.
  const [contractMonthMappings, setContractMonthMappings] = useState<CommodityContractMonthMappingModel[]>([]);

  useEffect(() => {
    listCommodityContractMonthMappings(null)
      .then((response) => setContractMonthMappings(response.data.rows ?? []))
      .catch(() => enqueueSnackbar('Failed to load contract month mappings', { variant: 'warning' }));
  }, []);

  async function saveContractMonthMappings(commodityId: number, rows: ContractMonthMappingRow[]) {
    const mappings = toUpdateContractMonthMappings(rows);

    try {
      const response = await updateCommodityContractMonthMappings(null, commodityId, mappings);
      const saved = response.data.mappings ?? [];

      setContractMonthMappings((previous) => [
        ...previous.filter((mapping) => mapping.commodityId !== commodityId),
        ...saved,
      ]);

      enqueueSnackbar(
        mappings.length === 0 ? 'Contract month mapping removed' : 'Contract month mapping saved',
        { variant: 'success' },
      );
    } catch {
      enqueueSnackbar('Failed to save the contract month mapping', { variant: 'error' });
    }
  }

  const columnDefs: ColDef[] = [
    {
      headerName: 'Commodity Name',
      field: 'commodityName',
      sortable: true,
      flex: 1,
      filter: false,
    },
    {
      headerName: 'Display Unit',
      field: 'displayUnit',
      sortable: true,
      width: 160,
      filter: false,
    },
    {
      headerName: 'Display Precision',
      field: 'displayPrecision',
      sortable: true,
      width: 160,
      filter: false,
    },
    {
      headerName: 'Contract Months',
      width: 170,
      filter: false,
      cellRenderer: (params: any) =>
        commodityHasContractMonthMapping(contractMonthMappings, params.data?.commodityId) ? (
          <Chip size="small" variant="outlined" color="info" label="Mapped" />
        ) : (
          <Chip size="small" variant="outlined" label="Same as budget" />
        ),
    },
    {
      headerName: 'Actions',
      width: 160,
      cellRenderer: (params: any) => {
        return (
          <ButtonGroup variant="text">
            <Button onClick={() => openEditModal(params.data)}>Edit</Button>
            <Button onClick={() => deleteCommodity(params.data)}>Delete</Button>
          </ButtonGroup>
        );
      },
    },
  ];

  function saveCommodity(commodityToSave: Partial<CommodityModel>) {
    if (!commodityToSave.commodityId) {
      CommodityManagementApi.addCommodity(
        saveCommodityLoadingState.setLoadingState,
        commodityToSave.commodityName!,
        commodityToSave.displayUnit!,
        commodityToSave.displayPrecision!,
      )
        .then((res) => {
          setCommodities((previous) => [...previous, res.data.commodity as CommodityModel]);
          dispatch(addCommodityRedux({ commodity: res.data.commodity! }));
          enqueueSnackbar('Commodity added successfully', { variant: 'success' });
        })
        .catch(() => {
          enqueueSnackbar('Failed to add commodity', { variant: 'error' });
        });
    } else {
      CommodityManagementApi.updateCommodity(
        saveCommodityLoadingState.setLoadingState,
        commodityToSave.commodityId!.toString(),
        commodityToSave.commodityName!,
        commodityToSave.displayUnit!,
        commodityToSave.displayPrecision!,
      )
        .then((res) => {
          setCommodities((previous) =>
            previous.map((commodity) =>
              commodity.commodityId == res.data.commodity?.commodityId ? (res.data.commodity as CommodityModel) : commodity,
            ),
          );
          dispatch(updateCommodityRedux({ commodity: res.data.commodity! }));
          enqueueSnackbar('Commodity updated successfully', { variant: 'success' });
        })
        .catch(() => {
          enqueueSnackbar('Failed to update commodity', { variant: 'error' });
        });
    }
  }

  function deleteCommodity(commodityToDelete: CommodityModel) {
    const commodityToDeleteId = commodityToDelete.commodityId!;

    CommodityManagementApi.deleteCommodity(null, commodityToDeleteId.toString())
      .then(() => {
        setCommodities((previous) => previous.filter((commodity) => commodity.commodityId != commodityToDeleteId));
        dispatch(removeCommodityRedux({ commodityId: commodityToDeleteId }));
        enqueueSnackbar('Commodity deleted successfully', { variant: 'success' });
      })
      .catch(() => {
        enqueueSnackbar('Failed to delete commodity. It may be in use by positions, budgets, or pricing strategies.', { variant: 'error' });
      });
  }

  function openAddModal() {
    setModalCommodity(undefined);
    setModalOpen(true);
  }

  function openEditModal(commodity: CommodityModel) {
    setModalCommodity({ ...commodity });
    setModalOpen(true);
  }

  return (
    <>
      <StoneXRow align="end">
        <h3>Commodities</h3>
        <Button className="pull-right" variant="outlined" onClick={openAddModal}>
          Add
        </Button>
      </StoneXRow>
      <div className="ag-theme-alpine" style={{ width: '100%' }}>
        <StoneXGrid
          hideGridOptions
          ref={gridRef}
          getRowId={(params: GetRowIdParams) => params.data.commodityId}
          columns={columnDefs}
          rows={commodities}
        />
      </div>
      <UpdateCommodityModal
        key={modalCommodity?.commodityId ?? 'new'}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCommoditySave={saveCommodity}
        commodity={modalCommodity}
        contractMonthMappings={contractMonthMappings}
        onSaveContractMonthMappings={saveContractMonthMappings}
      />
    </>
  );
}

interface UpdateCommodityProps {
  open: boolean;
  onClose: () => void;
  onCommoditySave: (commodity: Partial<CommodityModel>) => void;
  commodity?: CommodityModel;
  contractMonthMappings: CommodityContractMonthMappingModel[];
  onSaveContractMonthMappings: (commodityId: number, rows: ContractMonthMappingRow[]) => Promise<void>;
}

function UpdateCommodityModal(props: UpdateCommodityProps) {
  const { open, onClose, onCommoditySave, commodity, contractMonthMappings, onSaveContractMonthMappings } = props;
  const [commodityName, setCommodityName] = useState<string>(commodity?.commodityName ?? '');
  const [displayUnit, setDisplayUnit] = useState<Unit | null>(commodity?.displayUnit ?? null);
  const [displayPrecision, setDisplayPrecision] = useState<string>(
    (commodity?.displayPrecision ?? defaultDisplayPrecision).toString(),
  );
  const actionLabel = commodity ? 'Update' : 'Add';

  const hasSavedMapping = commodityHasContractMonthMapping(contractMonthMappings, commodity?.commodityId);

  // Hidden unless this commodity already has one. Most commodities price against their
  // own budget month, and those never need to see this table.
  const [showMapping, setShowMapping] = useState<boolean>(hasSavedMapping);
  const [mappingRows, setMappingRows] = useState<ContractMonthMappingRow[]>(() =>
    buildContractMonthMappingRows(contractMonthMappings, commodity?.commodityId),
  );
  const [isSavingMapping, setIsSavingMapping] = useState(false);

  useEffect(() => {
    setCommodityName(commodity?.commodityName ?? '');
    setDisplayUnit(commodity?.displayUnit ?? null);
    setDisplayPrecision((commodity?.displayPrecision ?? defaultDisplayPrecision).toString());
    setShowMapping(commodityHasContractMonthMapping(contractMonthMappings, commodity?.commodityId));
    setMappingRows(buildContractMonthMappingRows(contractMonthMappings, commodity?.commodityId));
  }, [commodity, contractMonthMappings]);

  const parsedPrecision = Number(displayPrecision);
  const precisionIsValid = displayPrecision.trim() !== '' && Number.isInteger(parsedPrecision) && parsedPrecision >= 0;
  const canSave = commodityName.trim() !== '' && displayUnit !== null && precisionIsValid;

  async function updateCommodity() {
    onCommoditySave({
      commodityId: commodity?.commodityId ?? 0,
      commodityName: commodityName.trim(),
      displayUnit: displayUnit!,
      displayPrecision: parsedPrecision,
    });

    // Only touch the mapping when there is something to say: the section is open, or it
    // was open before and the user removed it (which saves an empty set).
    const commodityId = commodity?.commodityId;
    if (commodityId && (showMapping || hasSavedMapping)) {
      setIsSavingMapping(true);
      try {
        await onSaveContractMonthMappings(commodityId, showMapping ? mappingRows : []);
      } finally {
        setIsSavingMapping(false);
      }
    }

    onClose();
  }

  return (
    <StoneXModal header={actionLabel + ' Commodity'} open={open} onClose={onClose}>
      <StoneXRow>
        <StoneXTextField label="Commodity Name" value={commodityName} onUpdate={(value) => setCommodityName(value)} />
      </StoneXRow>

      <StoneXRow>
        <StoneXSelect
          label="Display Unit"
          options={Object.values(Unit)}
          getId={(x) => x}
          getOptionLabel={(x) => x}
          value={displayUnit}
          onChange={(value) => setDisplayUnit(value)}
          width="small"
        />
      </StoneXRow>

      <StoneXRow>
        <StoneXTextField
          label="Display Precision"
          type="number"
          min={0}
          value={displayPrecision}
          onUpdate={(value) => setDisplayPrecision(value)}
          error={precisionIsValid ? undefined : 'Enter a whole number of 0 or more.'}
        />
      </StoneXRow>

      {commodity ? (
        <>
          <StoneXRow align="baseline">
            <h4 style={{ marginBottom: 0 }}>Contract Months</h4>
            {showMapping ? (
              <Button className="pull-right" variant="text" onClick={() => setShowMapping(false)}>
                Remove contract month mapping
              </Button>
            ) : (
              <Button className="pull-right" variant="text" onClick={() => setShowMapping(true)}>
                Add contract month mapping
              </Button>
            )}
          </StoneXRow>

          {showMapping ? (
            <ContractMonthMappingTable rows={mappingRows} disabled={isSavingMapping} onChange={setMappingRows} />
          ) : (
            <Alert severity="info" variant="outlined">
              {hasSavedMapping
                ? 'The saved mapping will be removed when you save. Budgets will price against their own budget month again.'
                : 'Budgets for this commodity price against their own budget month. Add a mapping only if a budget month prices against a different contract month - January budgeted corn priced off the March contract, for example.'}
            </Alert>
          )}
        </>
      ) : (
        <Alert severity="info" variant="outlined">
          Contract months can be mapped once the commodity has been added.
        </Alert>
      )}

      <StoneXRow justify="end">
        <Button variant="outlined" onClick={onClose} disabled={isSavingMapping}>
          Cancel
        </Button>
        <Button onClick={updateCommodity} disabled={!canSave || isSavingMapping}>
          {actionLabel + ' Commodity'}
        </Button>
      </StoneXRow>
    </StoneXModal>
  );
}
